import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ViteEnv } from '@/env';
import { useToast } from '@/hooks/use-toast';
import { supabaseClient } from '@/lib/supabase';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

export const ForgotPasswordForm = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const { toast } = useToast();
  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['auth', 'forgot-password'],
    mutationFn: async (values: FormValues) => {
      const { error } = await supabaseClient.auth.resetPasswordForEmail(
        values.email,
        {
          redirectTo: `${ViteEnv.VITE_APP_URL}/reset-password?email=${encodeURIComponent(values.email)}`,
        },
      );

      if (error !== null) {
        throw error;
      }
    },
    onError: () => {
      toast({
        title: 'auth.forgotPassword.form.submit.error',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'auth.forgotPassword.form.submit.success',
        variant: 'default',
      });
    },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-4 md:space-y-6"
        onSubmit={form.handleSubmit(async values => {
          await mutateAsync(values);
        })}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                <FormattedMessage id="auth.forgotPassword.form.email.label" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  type="text"
                  placeholder={formatMessage({
                    id: 'auth.forgotPassword.form.email.placeholder',
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 mt-0"
          disabled={isPending}
        >
          {isPending ? (
            <FormattedMessage id="auth.forgotPassword.form.submit.pending" />
          ) : (
            <FormattedMessage id="auth.forgotPassword.form.submit.label" />
          )}
        </Button>
      </form>
    </Form>
  );
};
